<template>

  <section class="w-full flex flex-col">
    <!-- Payment Link Modal -->
    <Dialog v-model:visible="showPaymentModal" :header="t('registerBotLanding.form.paymentRequired')" :modal="true" :dismissableMask="false" :closable="false" style="width: 50vw; max-width: 600px;">
      <div class="flex flex-col gap-6 pt-4 text-center">
        <i class="pi pi-check-circle text-green-500 mx-auto" style="font-size: 3rem;"></i>
        <h3 class="text-xl font-bold text-gray-800">{{ t('registerBotLanding.form.applicationSuccess') }}</h3>
        
        <div class="bg-blue-50 p-6 rounded-xl border border-blue-100 flex flex-col gap-3">
          <p class="text-blue-900 font-semibold">{{ t('registerBotLanding.form.paymentNextSteps') }}</p>
          <div class="flex items-center justify-between mt-2 pt-2 border-t border-blue-200">
            <span class="text-blue-800">{{ t('bm.pricing.total.title') }}:</span>
            <span class="text-2xl font-bold text-[#3070b6]">175 EUR</span>
          </div>
          <p class="text-sm text-blue-600 font-medium">{{ t('bm.pricing.total.breakdown') }}</p>
          <p class="text-xs text-blue-500 italic mt-2">{{ t('registerBotLanding.pricing.vatNote') }}</p>
        </div>

        <div class="flex flex-col sm:flex-row gap-4 mt-4 justify-center">
            <Button :label="t('registerBotLanding.form.payWithCard')" icon="pi pi-credit-card" @click="payWithWhop" class="w-full sm:w-auto p-button-lg" style="background-color: #3070b6; border-color: #3070b6" />
            <Button :label="t('registerBotLanding.form.payByInvoice')" icon="pi pi-file-invoice" severity="secondary" @click="payWithSwift" class="w-full sm:w-auto p-button-lg p-button-outlined" />
        </div>
      </div>
    </Dialog>

    <div class="flex sm:mx-auto pt-4 w-full h-max">
      <div class="w-full p-2 flex flex-col gap-4 h-max">
        <label for="client-title" class="flex flex-col">
          <span class="pl-1">{{ t('addAccount.infoTitle') }}</span>
          <InputText id="client-title" v-model="newClient.title" :placeholder="t('addAccount.infoTitle')" />
          <small v-for="error in v$.title.$errors" class="text-red-500 text-xs">{{ error.$message }}</small>
        </label>
        <label for="client-about" class="flex flex-col">
          <span class="pl-1">{{ t('addAccount.about') }}</span>
          <InputText id="client-about" v-model="newClient.about" :placeholder="t('addAccount.about')" />
          <small v-for="error in v$.about.$errors" class="text-red-500 text-xs">{{ error.$message }}</small>
        </label>
        <label for="client-address" class="flex flex-col">
          <span class="pl-1">{{ t('global.address') }}</span>
          <InputText id="client-address" v-model="newClient.address" :placeholder="t('global.address')" />
          <small v-for="error in v$.address.$errors" class="text-red-500 text-xs">{{ error.$message }}</small>
        </label>
        <label for="client-phone" class="flex flex-col">
          <span class="pl-1">{{ t('global.phone') }}</span>
          <InputText id="client-phone" v-model="newClient.phone" :placeholder="t('global.phone')" />
          <small v-for="error in v$.phone.$errors" class="text-red-500 text-xs">{{ error.$message }}</small>
        </label>
        <label for="client-website" class="flex flex-col">
          <span class="pl-1">{{ t('addAccount.website') }}</span>
          <InputText id="client-website" v-model="newClient.website" :placeholder="t('addAccount.website')" />
        </label>
        <label for="client-referral" class="flex flex-col">
          <span class="pl-1">{{ t('global.referralGuid') }}</span>
          <InputText id="client-referral" v-model="newClient.referralId" :placeholder="t('global.referralGuid')" />
        </label>
        <Button :label="t('actions.create')" @click="sendRequest()" :disabled="isFetching" />
      </div>
    </div>
  </section>

</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

const { t } = useI18n()
const isFetching = ref(false)
const url = useRuntimeConfig().public.baseURL

type CreateAccountApplication = {
  title: string
  about: string
  address: string
  phone: string
  website: string
  referralId: string | null
}

const newClient = ref<CreateAccountApplication>({
  title: '',
  about: '',
  address: '',
  phone: '',
  website: '',
  referralId: null
})

const rules = {
  title: {
    required: helpers.withMessage(() => t('validation.fieldReq'), required)
  },
  about: {
    required: helpers.withMessage(() => t('validation.fieldReq'), required),
  },
  phone: {
    required: helpers.withMessage(() => t('validation.fieldReq'), required)
  },
  address: {
    required: helpers.withMessage(() => t('validation.fieldReq'), required)
  },
};

const v$ = useVuelidate(rules, newClient);

const resetNewClient = () => {
  newClient.value.title = ''
  newClient.value.about = ''
  newClient.value.address = ''
  newClient.value.phone = ''
  newClient.value.website = ''
  newClient.value.referralId = ''
}

const sendRequest = async () => {
  const isValid = await v$.value.$validate();
  if (isValid) {
    try {
      isFetching.value = true

      const formD = new FormData()
      formD.append('Title', newClient.value.title)
      formD.append('About', newClient.value.about)
      formD.append('Address', newClient.value.address)
      formD.append('Phone', newClient.value.phone)
      if (newClient.value.website) formD.append('Website', newClient.value.website)
      if (newClient.value.referralId) formD.append('ReferralId', newClient.value.referralId)

      await $fetch(`${url}/account-applications`, { // Assuming the CA endpoint uses similar anonymous logic or standard creation
        method: 'POST',
        body: formD,
        onResponse: ({ response }) => {
            if (response.status == 200 || response.status == 201) {
               showPaymentModal.value = true;
            }
        }
      })
      
    } catch (error) {
       console.error(error)
    } finally {
      isFetching.value = false
    }
  }
};

const showPaymentModal = ref(false);

const payWithWhop = () => {
  window.open('https://whop.com/checkout/plan_aINUp1qE7xuLd', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewClient();
  v$.value.$reset();
};

const payWithSwift = () => {
  window.open('viber://chat?service=26076', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewClient();
  v$.value.$reset();
};
</script>